<template>
  <div class="min-h-screen flex">
    <!-- Right Side - Signup Form -->
    <div
      class="w-full lg:w-full flex items-center justify-center p-8 sm:p-12 bg-gray-50 dark:bg-gray-900"
    >
      <div class="w-full max-w-xl space-y-8">
        <!-- Mobile Logo -->
        <div class="text-center">
          <nuxt-link to="/">
            <img
              src="../../assets/images/logo-black.svg"
              alt="Academic Alerts"
              class="h-8 w-auto mx-auto mb-6"
            />
          </nuxt-link>
        </div>
        <hr />
        <div class="text-center">
          <h2 class="text-3xl font-bold text-gray-900 dark:text-white">
            Sign up for free
          </h2>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Already have an account?
            <nuxt-link
              to="/auth/login"
              class="font-medium text-primary hover:text-primary/80"
            >
              Sign in
            </nuxt-link>
          </p>
        </div>

        <form @submit.prevent="handleSignup" class="mt-8 space-y-6">
          <div class="space-y-5">
            <!-- Name Fields Row -->
            <div class="grid grid-cols-1 sm:grid-cols-5 gap-4">
              <!-- Title Input -->
              <div class="sm:col-span-1">
                <label
                  for="title"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Title
                </label>
                <div class="relative">
                  <select
                    id="title"
                    v-model="formData.title"
                    required
                    class="pl-9 block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm py-2.5 text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary dark:bg-gray-800 dark:text-white"
                  >
                    <option value="">Title</option>
                    <option value="Dr">Dr.</option>
                    <option value="Prof">Prof.</option>
                    <option value="Mr">Mr.</option>
                    <option value="Mrs">Mrs.</option>
                    <option value="Ms">Ms.</option>
                  </select>
                  <div
                    class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none"
                  >
                    <Icon
                      name="heroicons:academic-cap"
                      class="h-4 w-4 text-gray-400"
                    />
                  </div>
                </div>
              </div>

              <!-- First Name Input -->
              <div class="sm:col-span-2">
                <label
                  for="firstName"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  First Name
                </label>
                <div class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none"
                  >
                    <Icon name="heroicons:user" class="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    id="firstName"
                    v-model="formData.first_name"
                    type="text"
                    required
                    class="pl-9 block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm py-2.5 text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary dark:bg-gray-800 dark:text-white"
                    placeholder="First name"
                  />
                </div>
              </div>

              <!-- Last Name Input -->
              <div class="sm:col-span-2">
                <label
                  for="lastName"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Last Name
                </label>
                <div class="relative">
                  <div
                    class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none"
                  >
                    <Icon name="heroicons:user" class="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    id="lastName"
                    v-model="formData.last_name"
                    type="text"
                    required
                    class="pl-9 block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm py-2.5 text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary dark:bg-gray-800 dark:text-white"
                    placeholder="Last name"
                  />
                </div>
              </div>
            </div>

            <!-- Email Input -->
            <div>
              <label
                for="email"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Email address
              </label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none"
                >
                  <Icon
                    name="heroicons:envelope"
                    class="h-4 w-4 text-gray-400"
                  />
                </div>
                <input
                  id="email"
                  v-model="formData.email"
                  type="email"
                  required
                  class="pl-9 block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm py-2.5 text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary dark:bg-gray-800 dark:text-white"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <!-- Password Input -->
            <div>
              <label
                for="password"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Password
              </label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none"
                >
                  <Icon
                    name="heroicons:lock-closed"
                    class="h-4 w-4 text-gray-400"
                  />
                </div>
                <input
                  id="password"
                  v-model="formData.password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  minlength="6"
                  class="pl-9 block w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm py-2.5 text-sm placeholder-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary dark:bg-gray-800 dark:text-white"
                  placeholder="Create a password (min. 6 characters)"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-2.5 flex items-center"
                >
                  <Icon
                    :name="
                      showPassword ? 'heroicons:eye-slash' : 'heroicons:eye'
                    "
                    class="h-4 w-4 text-gray-400 hover:text-gray-600"
                  />
                </button>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div
            v-if="error"
            class="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg p-3 text-center"
          >
            {{ error }}
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="true"
            class="relative w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 group"
          >
            <span class="flex items-center">
              <Icon
                v-if="isLoading"
                name="heroicons:arrow-path"
                class="animate-spin h-4 w-4 mr-2"
              />
              {{ isLoading ? "Creating account..." : "Create account" }}
            </span>
          </button>

          <div class="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
            <div class="flex items-start gap-3">
              <Icon name="heroicons:information-circle" class="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 class="text-sm font-medium text-amber-800 dark:text-amber-300">Invite-Only Access</h3>
                <p class="mt-1 text-sm text-amber-700 dark:text-amber-400">
                  Academic Alerts is currently in a closed beta phase. Please contact us to request an invitation to join our academic community.
                </p>
              </div>
            </div>
          </div>
        </form>

        <div class="mt-8 space-y-6 hidden">
          <div
            class="bg-blue-50 dark:bg-blue-900/30 p-6 rounded-xl text-center space-y-4"
          >
            <div class="flex justify-center">
              <Icon name="heroicons:clock" class="w-12 h-12 text-primary" />
            </div>
            <h3 class="text-xl font-semibold text-primary">Coming Soon!</h3>
            <p class="text-gray-600 dark:text-gray-400">
              We're currently putting the finishing touches on our registration
              system. Check back soon to create your account and join our
              academic community.
            </p>
            <nuxt-link
              to="/"
              class="mt-4 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
            >
              <Icon name="heroicons:arrow-left" class="w-4 h-4 mr-2" />
              Back to Home
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const router = useRouter();
const { register } = useStrapiAuth();

const formData = ref({
  title: "",
  firstName: "",
  lastName: "",
  email: "",
  undefined: "",
  password: "",
});

const showPassword = ref(false);
const isLoading = ref(false);
const error = ref(null);

async function handleSignup() {
  error.value = null;
  isLoading.value = true;

  if (formData.value.password.length < 6) {
    error.value = "Password must be at least 6 characters long";
    isLoading.value = false;
    return;
  }

  formData.value.username = formData.value.email.toLowerCase();
  try {
    await register(formData.value);
    router.push("/dashboard");
  } catch (e) {
    error.value = "Registration failed. Please try again.";
  } finally {
    isLoading.value = false;
  }
}

// Reset error when form data changes
watch(
  formData,
  () => {
    error.value = null;
  },
  { deep: true }
);
</script>

<style scoped>
@keyframes gradient {
  0% {
    opacity: 0.9;
  }
  50% {
    opacity: 0.95;
  }
  100% {
    opacity: 0.9;
  }
}

.animate-gradient {
  animation: gradient 8s ease infinite;
}

.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
